import type { DocumentManagerCapability, PluginRegistry, ZoomLevel } from '@embedpdf/react-pdf-viewer';
import type { AxiosResponse } from 'axios';
import aosphereWordmark from '../assets/images/newdesign/aosphere_an_affiliate_logo.svg';
import { V2ApiClient } from '../services/axiosConfig';
import { PDF_VIEWER_ZOOM_LEVEL_KEY, RANGE_CHUNK_SIZE } from './constants';

// --- URL parsing ---

const PAGE_HASH_PATTERN = /#page=(\d+)/i;
const PAGE_QUERY_PATTERN = /[&?]page=(\d+)/i;

export interface ParsedPdfViewerUrl {
  pdfSrc: string;
  pageNumber: number;
}

function parsePageFromValue(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }

  const fromHash = PAGE_HASH_PATTERN.exec(value);
  if (fromHash?.[1]) {
    const parsed = Number.parseInt(fromHash[1], 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
  }

  const fromQuery = PAGE_QUERY_PATTERN.exec(value);
  if (fromQuery?.[1]) {
    const parsed = Number.parseInt(fromQuery[1], 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
  }

  return null;
}

/**
 * Splits an API/download URL like `abc.pdf#page=3` into a fetchable PDF URL and page index.
 * EmbedPDF does not read `#page=` — pass `pdfSrc` to `config.src` and scroll via the plugin.
 */
export function parsePdfViewerSource(
  rawUrl: string | null | undefined,
  locationHash?: string,
): ParsedPdfViewerUrl | null {
  const trimmed = rawUrl?.trim();
  if (!trimmed) {
    return null;
  }

  const pageNumber =
    parsePageFromValue(trimmed) ??
    parsePageFromValue(locationHash) ??
    1;

  const pdfSource = trimmed.replace(/#.*$/, '');

  if (!pdfSource) {
    return null;
  }

  return { pdfSrc: pdfSource, pageNumber };
}

// --- Toolbar permissions ---

/** `true` = show toolbar feature; `false` = hide. */
export interface PdfViewerFeaturePermissions {
  showSearch: boolean;
  showDownload: boolean;
  showPrint: boolean;
  showCopy: boolean;
}

export const PDF_VIEWER_FEATURE_IDS = {
  download: 101,
  print: 102,
  search: 103,
  copy: 104,
} as const;

/**
 * Frontend override for PDF search.
 * true  → search always shown, regardless of API feature 103.
 * false → search follows API filePermission.features (feature 103) only.
 */
export const PDF_VIEWER_FRONTEND_SEARCH_ENABLED = true;

export const PDF_VIEWER_PERMISSIONS_CONFIG: PdfViewerFeaturePermissions = {
  showSearch: PDF_VIEWER_FRONTEND_SEARCH_ENABLED,
  showDownload: false,
  showPrint: false,
  showCopy: false,
};

function isFeatureEnabled(
  features: Array<{ featureId: number; isEnabled: boolean }> | undefined,
  featureId: number,
): boolean {
  const feature = features?.find((item) => item.featureId === featureId);
  return feature?.isEnabled ?? false;
}

/** Maps API `filePermission.features` to EmbedPDF toolbar permissions. */
export function buildPdfViewerPermissionsFromFilePermission(
  filePermission: { features?: Array<{ featureId: number; isEnabled: boolean }> } | null | undefined,
): PdfViewerFeaturePermissions {
  if (!filePermission?.features?.length) {
    return PDF_VIEWER_PERMISSIONS_CONFIG;
  }

  return {
    showDownload: isFeatureEnabled(filePermission.features, PDF_VIEWER_FEATURE_IDS.download),
    showPrint: isFeatureEnabled(filePermission.features, PDF_VIEWER_FEATURE_IDS.print),
    showSearch:
      PDF_VIEWER_FRONTEND_SEARCH_ENABLED ||
      isFeatureEnabled(filePermission.features, PDF_VIEWER_FEATURE_IDS.search),
    showCopy: isFeatureEnabled(filePermission.features, PDF_VIEWER_FEATURE_IDS.copy),
  };
}

/**
 * Categories always hidden (not permission-driven).
 * Do not add parent `document` here — it hides print/export children even when enabled.
 */
const PDF_VIEWER_BASE_DISABLED_CATEGORIES = [
  'annotation',
  'annotation-shape',
  'insert',
  'form',
  'redaction',
  'history',
  'document-open',
  'document-close',
  'document-capture',
  'document-fullscreen',
  'document-protect',
  'capture',
  'security-unlock-overlay',
  'page-settings',
  'pan',
  'pointer',
  'panel-comment',
  'tools',
] as const;

const FEATURE_DISABLE_CATEGORY: Record<keyof PdfViewerFeaturePermissions, string> = {
  showSearch: 'panel-search',
  showDownload: 'document-export',
  showPrint: 'document-print',
  showCopy: 'selection-copy',
};

export function buildPdfViewerDisabledCategories(
  features: PdfViewerFeaturePermissions,
): Array<string> {
  const disabled: Array<string> = [...PDF_VIEWER_BASE_DISABLED_CATEGORIES];

  if (!features.showSearch) {
    disabled.push(FEATURE_DISABLE_CATEGORY.showSearch);
  }

  if (!features.showCopy) {
    disabled.push('selection', FEATURE_DISABLE_CATEGORY.showCopy);
  }

  const showDocumentMenu = features.showDownload || features.showPrint;
  if (showDocumentMenu) {
    if (!features.showDownload) {
      disabled.push(FEATURE_DISABLE_CATEGORY.showDownload);
    }
    if (!features.showPrint) {
      disabled.push(FEATURE_DISABLE_CATEGORY.showPrint);
    }
  } else {
    disabled.push('document');
  }

  return disabled;
}

// --- PDF download (range requests) ---

const inFlightLoads = new Map<string, Promise<Blob>>();

async function fetchStoragePdfRange(
  storagePdfPath: string,
  begin: number,
  endExclusive: number,
): Promise<AxiosResponse<Blob>> {
  return V2ApiClient.get<Blob>(storagePdfPath, {
    responseType: 'blob',
    headers: { 'X-Range': `bytes=${begin}-${endExclusive - 1}` },
    validateStatus: (status) => status === 200 || status === 206,
  });
}

async function downloadStoragePdf(storagePdfPath: string): Promise<Blob> {
  const chunks: Array<Blob> = [];
  let begin = 0;

  while (true) {
    const endExclusive = begin + RANGE_CHUNK_SIZE;
    // Ranges are sequential: each request's start depends on the previous chunk size.
    // eslint-disable-next-line no-await-in-loop
    const resp = await fetchStoragePdfRange(storagePdfPath, begin, endExclusive);

    if (resp.status === 200) {
      return resp.data;
    }

    if (resp.status !== 206) {
      throw new Error(`Unexpected PDF response status: ${resp.status}`);
    }

    chunks.push(resp.data);

    if (resp.data.size < RANGE_CHUNK_SIZE) {
      return new Blob(chunks, { type: 'application/pdf' });
    }

    begin += resp.data.size;
  }
}

/**
 * Fetches a storage PDF for EmbedPDF (`PDF_LoadMemDocument` needs a complete blob).
 * Path is relative to the V2 API base, e.g. `storage/download/....pdf?download=false`.
 */
export async function getStoragePdfBlob(storagePdfPath: string): Promise<Blob> {
  const existing = inFlightLoads.get(storagePdfPath);
  if (existing) {
    return existing;
  }

  const load = downloadStoragePdf(storagePdfPath);
  inFlightLoads.set(storagePdfPath, load);

  try {
    return await load;
  } finally {
    inFlightLoads.delete(storagePdfPath);
  }
}

// --- Content protection ---

const NO_SELECT_STYLE_ID = 'pdf-viewer-no-select';
const UI_TWEAKS_STYLE_ID = 'pdf-viewer-ui-tweaks';

const NO_SELECT_CSS = `
  :host, *:not(input):not(textarea):not([contenteditable='true']) {
    user-select: none !important;
    -webkit-user-select: none !important;
  }
  input, textarea, [contenteditable='true'] {
    user-select: text !important;
    -webkit-user-select: text !important;
  }
`;

/** Search / contents panel density + type scale (≈2 steps smaller). */
const UI_TWEAKS_CSS = `
  [data-epdf-i="divider-1"],
  [data-epdf-i="divider-2"],
  [data-epdf-i="divider-3"],
  [data-epdf-i="divider-8"],
  [data-epdf-i="divider-9"],
  [data-epdf-i="divider-10"],
  [data-epdf-i="divider-11"],
  [data-epdf-i="divider-13"],
  [data-epdf-i="divider-14"],
  [data-epdf-i="divider-15"],
  [data-epdf] hr {
    display: none !important;
  }
  /* Leave room for brand wordmark after the sidebar button. */
  [data-epdf-i="left-group"] {
    margin-right: 100px !important;
  }
  /* Search panel */
  [data-epdf] input.text-base {
    font-size: 0.75rem !important;
    line-height: 1.25 !important;
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  [data-epdf] .text-fg-muted.text-xs,
  [data-epdf] .bg-bg-surface\\/80.text-xs {
    font-size: 0.625rem !important;
    line-height: 1.25 !important;
  }
  [data-epdf] button.w-full.rounded.border.text-sm {
    font-size: 0.75rem !important;
    line-height: 1.25 !important;
    padding: 0.375rem !important;
  }
  [data-epdf] .p-4 {
    padding: 0.5rem !important;
  }
  [data-epdf] .px-4 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  [data-epdf] .mb-2.mt-5 {
    margin-top: 0.5rem !important;
    margin-bottom: 0.25rem !important;
  }
  [data-epdf] .gap-2 {
    gap: 0.25rem !important;
  }
  /* Contents / outline */
  [data-epdf] .outline-tree,
  [data-epdf] .outline-tree button,
  [data-epdf] .outline-tree .text-sm {
    font-size: 0.75rem !important;
    line-height: 1.25 !important;
  }
  /* Instant page jumps (ToC / search / cross-refs) — never CSS-smooth scroll. */
  * {
    scroll-behavior: auto !important;
  }
`;

function getDeepActiveElement(): Element | null {
  let active: Element | null = document.activeElement;

  while (active?.shadowRoot?.activeElement instanceof Element) {
    active = active.shadowRoot.activeElement;
  }

  return active;
}

function isEditableNode(node: EventTarget): boolean {
  if (!(node instanceof Element)) {
    return false;
  }

  return (
    node instanceof HTMLInputElement ||
    node instanceof HTMLTextAreaElement ||
    (node instanceof HTMLElement && node.isContentEditable) ||
    node.closest('input, textarea, [contenteditable="true"]') !== null
  );
}

function blockPdfViewerSelectStart(event: Event): void {
  if (event.composedPath().some((node) => isEditableNode(node))) {
    return;
  }

  event.preventDefault();
}

function clearPdfViewerDocumentSelection(): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const anchorNode = selection.anchorNode;
  const anchorElement =
    anchorNode?.nodeType === Node.TEXT_NODE
      ? anchorNode.parentElement
      : anchorNode instanceof Element
        ? anchorNode
        : null;

  if (isEditableNode(getDeepActiveElement() ?? document.body)) {
    return;
  }

  if (anchorElement && isEditableNode(anchorElement)) {
    return;
  }

  selection.removeAllRanges();
}

function isBlockedKeyboardShortcut(event: KeyboardEvent): boolean {
  const key = event.key.toLowerCase();
  const modifier = event.ctrlKey || event.metaKey;

  if (modifier && !event.shiftKey && key === 'f') {
    return true;
  }

  if (key === 'f12') {
    return true;
  }

  return modifier && event.shiftKey && (key === 'i' || key === 'j' || key === 'c');
}

function getCtrlZoomDirection(event: KeyboardEvent): -1 | 0 | 1 {
  if (!event.ctrlKey && !event.metaKey) {
    return 0;
  }
  if (event.key === '+' || event.key === '=' || event.code === 'NumpadAdd') {
    return 1;
  }
  if (event.key === '-' || event.key === '_' || event.code === 'NumpadSubtract') {
    return -1;
  }
  return 0;
}

function isBrowserZoomShortcut(event: KeyboardEvent): boolean {
  if (!event.ctrlKey && !event.metaKey) {
    return false;
  }
  return event.key === '0' || event.code === 'Numpad0' || getCtrlZoomDirection(event) !== 0;
}

function handlePdfViewerKeyDown(event: KeyboardEvent): void {
  if (isBlockedKeyboardShortcut(event) || isBrowserZoomShortcut(event)) {
    event.preventDefault();
  }
}

/** Blocks browser find and common DevTools shortcuts on the PDF viewer page. */
export function installPdfViewerKeyboardGuards(): () => void {
  document.addEventListener('keydown', handlePdfViewerKeyDown, true);
  return () => document.removeEventListener('keydown', handlePdfViewerKeyDown, true);
}

let pdfViewerZoomInputCleanup: (() => void) | undefined;

export function cleanupPdfViewerZoomInputGuards(): void {
  pdfViewerZoomInputCleanup?.();
  pdfViewerZoomInputCleanup = undefined;
}

function isPrintScreenKey(event: KeyboardEvent): boolean {
  return event.key === 'PrintScreen' || event.code === 'PrintScreen';
}

function isWinShiftHeld(event: KeyboardEvent): boolean {
  return event.metaKey && event.shiftKey && !event.ctrlKey && !event.altKey;
}

function isScreenshotShortcut(event: KeyboardEvent): boolean {
  if (isPrintScreenKey(event)) {
    return true;
  }

  const key = event.key.toLowerCase();
  const code = event.code;
  return isWinShiftHeld(event) && (key === 's' || key === 'r' || code === 'KeyS' || code === 'KeyR');
}

const SCREENSHOT_BLOCKED_CLASS = 'pdf-viewer-screenshot-blocked';

/**
 * Best-effort screenshot deterrent.
 * Blanks the PDF immediately via DOM (same tick as the key event) so Snipping Tool
 * cannot select content while React is still re-rendering.
 *
 * Cannot fully stop OS capture; Win+Shift+S may still be stolen by Windows.
 */
export function installPdfViewerScreenshotGuards(
  root: HTMLElement,
  onBlocked: () => void,
): { cleanup: () => void; reset: () => void } {
  let hardBlocked = false;
  let preemptiveBlank = false;

  const blankNow = () => {
    root.classList.add(SCREENSHOT_BLOCKED_CLASS);
  };

  const clearBlank = () => {
    if (hardBlocked) {
      return;
    }
    root.classList.remove(SCREENSHOT_BLOCKED_CLASS);
  };

  const hardBlock = () => {
    blankNow();
    if (hardBlocked) {
      return;
    }
    hardBlocked = true;
    onBlocked();
  };

  const reset = () => {
    hardBlocked = false;
    preemptiveBlank = false;
    root.classList.remove(SCREENSHOT_BLOCKED_CLASS);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    // Blank as soon as Win+Shift is held (before S/R opens Snipping Tool).
    if (!hardBlocked && isWinShiftHeld(event)) {
      preemptiveBlank = true;
      blankNow();
    }

    if (!isScreenshotShortcut(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    hardBlock();
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    // PrintScreen often arrives on keyup (and after OS capture starts).
    if (isPrintScreenKey(event)) {
      event.preventDefault();
      hardBlock();
      return;
    }

    if (!preemptiveBlank || hardBlocked) {
      return;
    }

    // Win+Shift released without completing a screenshot shortcut — restore.
    if (!event.metaKey || !event.shiftKey) {
      preemptiveBlank = false;
      clearBlank();
    }
  };

  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('keyup', handleKeyUp, true);

  return {
    reset,
    cleanup: () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      root.classList.remove(SCREENSHOT_BLOCKED_CLASS);
    },
  };
}

function visitShadowRoots(element: Element, visitor: (shadowRoot: ShadowRoot) => void): void {
  if (element.shadowRoot) {
    visitor(element.shadowRoot);
    element.shadowRoot.querySelectorAll('*').forEach((child) => {
      if (child instanceof Element) {
        visitShadowRoots(child, visitor);
      }
    });
  }

  element.querySelectorAll('*').forEach((child) => {
    if (child instanceof Element) {
      visitShadowRoots(child, visitor);
    }
  });
}

function injectShadowStyle(shadowRoot: ShadowRoot, id: string, css: string): void {
  const existingStyle = shadowRoot.querySelector<HTMLStyleElement>(`#${id}`);
  if (existingStyle) {
    existingStyle.textContent = css;
    return;
  }

  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  shadowRoot.append(style);
}

/** Injects UI density tweaks into EmbedPDF shadow roots; blocks text selection unless allowed. */
export function applyPdfViewerNoSelectStyles(
  root: HTMLElement,
  allowTextSelection = false,
): () => void {
  const inject = () => {
    visitShadowRoots(root, (shadowRoot) => {
      if (allowTextSelection) {
        shadowRoot.querySelector(`#${NO_SELECT_STYLE_ID}`)?.remove();
      } else {
        injectShadowStyle(shadowRoot, NO_SELECT_STYLE_ID, NO_SELECT_CSS);
      }
      injectShadowStyle(shadowRoot, UI_TWEAKS_STYLE_ID, UI_TWEAKS_CSS);
    });
  };

  inject();

  const observer = new MutationObserver(inject);
  observer.observe(root, { childList: true, subtree: true });

  if (!allowTextSelection) {
    root.addEventListener('selectstart', blockPdfViewerSelectStart, true);
    document.addEventListener('selectionchange', clearPdfViewerDocumentSelection);
  }

  return () => {
    observer.disconnect();
    if (!allowTextSelection) {
      root.removeEventListener('selectstart', blockPdfViewerSelectStart, true);
      document.removeEventListener('selectionchange', clearPdfViewerDocumentSelection);
    }
  };
}


// --- EmbedPDF UI ---

const PAGE_SETTINGS_TOOLBAR_IDS = new Set([
  'page-settings-button',
  'page-settings-submenu',
]);

/**
 * Orphan dividers left when sibling commands are category-disabled.
 * `divider-11` sits above fullscreen; fullscreen is always disabled → empty strip under Export.
 * Do not remove command items here — only the separator.
 */
const ORPHAN_DOCUMENT_MENU_DIVIDER_IDS = new Set(['divider-11']);

/** Vertical `|` after the hamburger; left behind when the whole document menu is hidden. */
const DOCUMENT_MENU_TOOLBAR_DIVIDER_ID = 'divider-1';

interface ToolbarLike {
  type: string;
  id?: string;
  commandId?: string;
  variant?: string;
  items?: Array<ToolbarLike>;
  alignment?: string;
  gap?: number;
  flex?: boolean;
  [key: string]: unknown;
}

// EmbedPDF's `getPlugin(...).provides()` is loosely typed; describe only what we use.
interface SelectionModeOptions {
  enableSelection: boolean;
  enableMarquee: boolean;
  showSelectionRects: boolean;
}

interface SelectionProvides {
  enableForMode: (modeId: string, options: SelectionModeOptions) => void;
  onSelectionChange: (
    handler: (event: { documentId: string; selection: unknown }) => void,
  ) => void;
  clear: (documentId: string) => void;
}

interface ToolbarEntry {
  items: Array<ToolbarLike>;
  [key: string]: unknown;
}

interface MenuEntry {
  id?: string;
  items: Array<ToolbarLike>;
  [key: string]: unknown;
}

interface SidebarTab {
  id?: string;
  labelKey?: string;
  label?: string;
  icon?: string;
  componentId?: string;
  [key: string]: unknown;
}

interface SidebarEntry {
  id?: string;
  width?: string;
  defaultOpen?: boolean;
  content?: {
    type?: string;
    tabs?: Array<SidebarTab>;
    componentId?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface UiSchema {
  toolbars: Record<string, ToolbarEntry | undefined>;
  menus?: Record<string, MenuEntry | undefined>;
  sidebars?: Record<string, SidebarEntry | undefined>;
}

interface UiProvides {
  getSchema?: () => UiSchema;
  mergeSchema?: (schema: {
    toolbars?: Record<string, ToolbarEntry>;
    menus?: Record<string, MenuEntry>;
    sidebars?: Record<string, SidebarEntry>;
  }) => void;
}

interface ScrollForDocument {
  scrollToPage: (options: { pageNumber: number; behavior: 'instant' | 'smooth' }) => void;
}

interface ScrollProvides {
  onLayoutReady?: (
    handler: (event: { documentId: string; isInitial: boolean }) => void,
  ) => void;
  forDocument: (documentId: string) => ScrollForDocument;
}

function getPluginProvides<T>(registry: PluginRegistry, pluginId: string): T | undefined {
  return registry.getPlugin(pluginId)?.provides?.() as T | undefined;
}

/** Force ToC / search / page / cross-ref jumps to land immediately (no smooth scroll). */
function forceEmbedPdfInstantNavigation(registry: PluginRegistry): void {
  const scrollPlugin = registry.getPlugin('scroll') as {
    scrollToPage?: (
      options: { pageNumber: number; behavior?: 'instant' | 'smooth' | 'auto' } & Record<string, unknown>,
      documentId?: string,
    ) => void;
  } | null;
  if (!scrollPlugin?.scrollToPage) {
    return;
  }
  const original = scrollPlugin.scrollToPage.bind(scrollPlugin);
  scrollPlugin.scrollToPage = (options, documentId) =>
    original({ ...options, behavior: 'instant' }, documentId);
}

function filterToolbarItems(
  items: Array<ToolbarLike>,
  orphanDividerIds: Set<string> = new Set(),
  removeAllDividers = false,
): Array<ToolbarLike> {
  return items
    .filter((item) => {
      if (item.id && PAGE_SETTINGS_TOOLBAR_IDS.has(item.id)) {
        return false;
      }
      if (item.type === 'divider') {
        if (removeAllDividers) {
          return false;
        }
        if (item.id && orphanDividerIds.has(item.id)) {
          return false;
        }
      }
      return true;
    })
    .map((item) => {
      if (item.items?.length) {
        return {
          ...item,
          items: filterToolbarItems(item.items, orphanDividerIds, removeAllDividers),
        };
      }
      return item;
    });
}

function configureEmbedPdfTextSelection(registry: PluginRegistry, allowCopy: boolean): void {
  const selection = getPluginProvides<SelectionProvides>(registry, 'selection');
  if (!selection) {
    return;
  }

  const applyForMode = (modeId: string) => {
    selection.enableForMode(modeId, {
      enableSelection: allowCopy,
      enableMarquee: false,
      showSelectionRects: allowCopy,
    });
  };

  applyForMode('pointer');
  applyForMode('pan');

  if (!allowCopy) {
    selection.onSelectionChange((event) => {
      if (event.selection) {
        selection.clear(event.documentId);
      }
    });
  }
}

/**
 * PDF Contents / TOC links are LINK annotations. EmbedPDF only navigates them when locked
 * (renderLocked overlay → navigateTarget). Interactive mode selects the annotation instead.
 */
function enableEmbedPdfDocumentLinkNavigation(registry: PluginRegistry): void {
  const annotation = getPluginProvides<{
    forDocument?: (documentId: string) => {
      setLocked?: (mode: { type: 'all' | 'none' | 'include' | 'exclude'; categories?: Array<string> }) => void;
    };
  }>(registry, 'annotation');
  const scroll = getPluginProvides<ScrollProvides>(registry, 'scroll');
  if (!annotation?.forDocument || !scroll?.onLayoutReady) {
    return;
  }

  scroll.onLayoutReady((event) => {
    if (!event.isInitial) {
      return;
    }
    annotation.forDocument!(event.documentId).setLocked?.({ type: 'all' });
  });
}

function buildMainToolbarItems(items: Array<ToolbarLike>): Array<ToolbarLike> {
  const leftGroup = items.find((item) => item.id === 'left-group');
  const centerGroup = items.find((item) => item.id === 'center-group');
  const rightGroup = items.find((item) => item.id === 'right-group');

  const sidebarButton = (leftGroup?.items ?? []).find((item) => item.id === 'sidebar-button');
  const searchButton = (rightGroup?.items ?? []).find((item) => item.id === 'search-button');
  const zoomItems = (centerGroup?.items ?? []).filter(
    (item) => item.id === 'zoom-toolbar' || item.id === 'zoom-menu-button',
  );

  const rightItems: Array<ToolbarLike> = [
    {
      type: 'command-button',
      id: 'export-button',
      commandId: 'document:export',
      variant: 'icon',
      categories: ['document', 'document-export'],
    },
    {
      type: 'command-button',
      id: 'print-button',
      commandId: 'document:print',
      variant: 'icon',
      categories: ['document', 'document-print'],
    },
  ];
  if (searchButton) {
    rightItems.push(searchButton);
  }

  return [
    {
      type: 'group',
      id: 'left-group',
      alignment: 'start',
      gap: 2,
      items: sidebarButton ? [sidebarButton] : [],
    },
    { type: 'spacer', id: 'spacer-1', flex: true },
    {
      type: 'group',
      id: 'center-group',
      alignment: 'center',
      gap: 2,
      items: zoomItems,
    },
    { type: 'spacer', id: 'spacer-2', flex: true },
    {
      type: 'group',
      id: 'right-group',
      alignment: 'end',
      gap: 2,
      items: rightItems,
    },
  ];
}

/** EmbedPDF toolbar lives in `embedpdf-container` open shadow root. */
let aosphereLogoPollTimer: ReturnType<typeof setInterval> | undefined;

function injectAosphereToolbarLogo(): void {
  const AOSPHERE_BRAND_LOGO_ID = 'aosphere-brand-logo';

  if (aosphereLogoPollTimer !== undefined) {
    globalThis.clearInterval(aosphereLogoPollTimer);
    aosphereLogoPollTimer = undefined;
  }

  const placeLogo = (): boolean => {
    const shadow = document.querySelector('embedpdf-container')?.shadowRoot;
    if (!shadow) {
      return false;
    }
    if (shadow.querySelector(`#${AOSPHERE_BRAND_LOGO_ID}`)) {
      return true;
    }

    const sidebar = shadow.querySelector('[data-epdf-i="sidebar-button"]');
    if (!sidebar) {
      return false;
    }

    const logo = document.createElement('img');
    logo.id = AOSPHERE_BRAND_LOGO_ID;
    logo.src = aosphereWordmark;
    logo.alt = '';
    logo.draggable = false;
    logo.setAttribute('aria-hidden', 'true');
    logo.style.cssText =
      'height:18px;width:auto;max-width:120px;margin:0 8px 0 4px;object-fit:contain;pointer-events:none;user-select:none;flex-shrink:0';
    sidebar.after(logo);
    return true;
  };

  if (placeLogo()) {
    return;
  }

  const startedAt = Date.now();
  aosphereLogoPollTimer = globalThis.setInterval(() => {
    if (placeLogo() || Date.now() - startedAt > 15_000) {
      globalThis.clearInterval(aosphereLogoPollTimer);
      aosphereLogoPollTimer = undefined;
    }
  }, 100);
}

function hideEmbedPdfOrphanSeparators(
  registry: PluginRegistry,
  features: PdfViewerFeaturePermissions,
): void {
  const ui = getPluginProvides<UiProvides>(registry, 'ui');
  if (!ui?.getSchema || !ui.mergeSchema) {
    return;
  }

  const schema = ui.getSchema();
  const mainToolbar = schema.toolbars['main-toolbar'];
  const documentMenu = schema.menus?.['document-menu'];
  const sidebarPanel = schema.sidebars?.['sidebar-panel'];
  const showDocumentMenu = features.showDownload || features.showPrint;

  const toolbars: Record<string, ToolbarEntry> = {};
  if (mainToolbar) {
    const orphanToolbarDividers = new Set<string>();
    if (!showDocumentMenu) {
      orphanToolbarDividers.add(DOCUMENT_MENU_TOOLBAR_DIVIDER_ID);
    }

    const clonedItems = structuredClone(mainToolbar.items) as Array<ToolbarLike>;
    const withoutOrphans = filterToolbarItems(clonedItems, orphanToolbarDividers, true);
    toolbars['main-toolbar'] = {
      ...mainToolbar,
      items: buildMainToolbarItems(withoutOrphans),
    };
  }

  const menus: Record<string, MenuEntry> = {};
  if (documentMenu) {
    const clonedItems = structuredClone(documentMenu.items) as Array<ToolbarLike>;
    menus['document-menu'] = {
      ...documentMenu,
      items: clonedItems.filter(
        (item) => !(item.type === 'divider' && item.id && ORPHAN_DOCUMENT_MENU_DIVIDER_IDS.has(item.id)),
      ),
    };
  }

  const sidebars: Record<string, SidebarEntry> = {};
  if (sidebarPanel) {
    // Default EmbedPDF width is 250px; +20% ≈ 300px for the contents menu.
    sidebars['sidebar-panel'] = {
      ...sidebarPanel,
      width: '300px',
      defaultOpen: true,
    };
  }

  if (
    Object.keys(toolbars).length === 0 &&
    Object.keys(menus).length === 0 &&
    Object.keys(sidebars).length === 0
  ) {
    return;
  }

  ui.mergeSchema({
    ...(Object.keys(toolbars).length > 0 ? { toolbars } : {}),
    ...(Object.keys(menus).length > 0 ? { menus } : {}),
    ...(Object.keys(sidebars).length > 0 ? { sidebars } : {}),
  });
}

type BookmarkTask = {
  wait: (
    onSuccess: (result: { bookmarks?: Array<unknown> }) => void,
    onError?: () => void,
  ) => void;
};

type BookmarkProvides = {
  getBookmarks?: () => BookmarkTask;
  forDocument?: (documentId: string) => { getBookmarks: () => BookmarkTask };
};

/** Prefer Outline when the PDF has bookmarks; hide the Outline tab when it does not.
 * Outline collapse + instant page jumps: `PDFViewerPage/pdfViewer.embedPdfOutlinePatch.mjs` (postinstall). */
function applyEmbedPdfSidebarOutlineAvailability(
  registry: PluginRegistry,
  hasOutline: boolean,
): void {
  const ui = getPluginProvides<UiProvides>(registry, 'ui');
  if (!ui?.getSchema || !ui.mergeSchema) {
    return;
  }

  const sidebarPanel = ui.getSchema().sidebars?.['sidebar-panel'];
  if (!sidebarPanel?.content || sidebarPanel.content.type !== 'tabs' || !sidebarPanel.content.tabs) {
    return;
  }

  const existingTabs = structuredClone(sidebarPanel.content.tabs) as Array<SidebarTab>;
  const thumbnailTab = existingTabs.find((tab) => tab.id === 'thumbnails');
  const outlineTab = existingTabs.find((tab) => tab.id === 'outline');
  const otherTabs = existingTabs.filter(
    (tab) => tab.id !== 'thumbnails' && tab.id !== 'outline',
  );
  const tabs: Array<SidebarTab> = hasOutline
    ? [...(outlineTab ? [outlineTab] : []), ...(thumbnailTab ? [thumbnailTab] : []), ...otherTabs]
    : [...(thumbnailTab ? [thumbnailTab] : []), ...otherTabs];

  ui.mergeSchema({
    sidebars: {
      'sidebar-panel': {
        ...sidebarPanel,
        width: sidebarPanel.width ?? '300px',
        defaultOpen: true,
        content: {
          ...sidebarPanel.content,
          type: 'tabs',
          tabs,
        },
      },
    },
  });
}

function openEmbedPdfSidebarByDefault(registry: PluginRegistry): void {
  const ui = getPluginProvides<{
    forDocument: (documentId: string) => {
      isSidebarOpen?: (placement: string, slot: string, sidebarId?: string) => boolean;
      toggleSidebar?: (placement: string, slot: string, sidebarId: string) => void;
      setSidebarTab?: (sidebarId: string, tabId: string) => void;
    };
  }>(registry, 'ui');
  const scroll = getPluginProvides<ScrollProvides>(registry, 'scroll');
  const bookmarkApi = getPluginProvides<BookmarkProvides>(registry, 'bookmark');
  if (!ui?.forDocument || !scroll?.onLayoutReady) {
    return;
  }

  scroll.onLayoutReady((event) => {
    if (!event.isInitial) {
      return;
    }

    const finish = (hasOutline: boolean) => {
      applyEmbedPdfSidebarOutlineAvailability(registry, hasOutline);
      const scope = ui.forDocument(event.documentId);
      scope.setSidebarTab?.('sidebar-panel', hasOutline ? 'outline' : 'thumbnails');
      if (!scope.toggleSidebar) {
        return;
      }
      if (scope.isSidebarOpen?.('left', 'main', 'sidebar-panel')) {
        return;
      }
      scope.toggleSidebar('left', 'main', 'sidebar-panel');
    };

    const task =
      bookmarkApi?.forDocument?.(event.documentId)?.getBookmarks() || bookmarkApi?.getBookmarks?.();
    if (!task) {
      finish(false);
      return;
    }
    task.wait(
      (result) => {
        finish((result.bookmarks?.length ?? 0) > 0);
      },
      () => {
        finish(false);
      },
    );
  });
}

function scrollEmbedPdfToPage(registry: PluginRegistry, pageNumber: number): void {
  if (pageNumber <= 1) {
    return;
  }

  const scroll = getPluginProvides<ScrollProvides>(registry, 'scroll');
  if (!scroll?.onLayoutReady) {
    return;
  }

  scroll.onLayoutReady((event) => {
    if (!event.isInitial) {
      return;
    }

    scroll.forDocument(event.documentId).scrollToPage({
      pageNumber,
      behavior: 'instant',
    });
  });
}

function getPdfViewerZoomLevel(): number | null {
  const value = localStorage.getItem(PDF_VIEWER_ZOOM_LEVEL_KEY);
  if (!value) {
    return null;
  }
  const zoom = Number(value);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : null;
}

function persistPdfViewerZoomLevel(zoomLevel: ZoomLevel): void {
  // Manual zoom (dropdown / % input / +/-) stores a numeric zoomLevel.
  // Automatic / Fit Width / Fit Page keep a mode string — do not overwrite storage.
  if (typeof zoomLevel !== 'number' || !Number.isFinite(zoomLevel) || zoomLevel <= 0) {
    return;
  }
  localStorage.setItem(PDF_VIEWER_ZOOM_LEVEL_KEY, String(zoomLevel));
}

function installPdfViewerZoomPersistence(registry: PluginRegistry): void {
  const zoom = getPluginProvides<{
    zoomIn?: () => void;
    zoomOut?: () => void;
    onStateChange?: (
      handler: (event: {
        documentId: string;
        state: { zoomLevel: ZoomLevel; currentZoomLevel: number };
      }) => void,
    ) => () => void;
    onZoomChange?: (handler: (event: { level: ZoomLevel; newZoom: number }) => void) => () => void;
    forDocument?: (documentId: string) => { requestZoom: (level: ZoomLevel) => void };
  }>(registry, 'zoom');
  const scroll = getPluginProvides<ScrollProvides>(registry, 'scroll');
  if (!zoom?.forDocument || !scroll?.onLayoutReady) {
    return;
  }

  if (zoom.onStateChange) {
    zoom.onStateChange((event) => {
      persistPdfViewerZoomLevel(event.state.zoomLevel);
    });
  } else if (zoom.onZoomChange) {
    zoom.onZoomChange((event) => {
      if (typeof event.level !== 'number') {
        return;
      }
      persistPdfViewerZoomLevel(event.newZoom);
    });
  } else {
    return;
  }

  scroll.onLayoutReady((event) => {
    if (!event.isInitial) {
      return;
    }
    const savedZoom = getPdfViewerZoomLevel();
    if (savedZoom == null) {
      return;
    }
    zoom.forDocument!(event.documentId).requestZoom(savedZoom);
  });

  if (!zoom.zoomIn || !zoom.zoomOut) {
    return;
  }

  pdfViewerZoomInputCleanup?.();
  const onKeyDown = (event: KeyboardEvent) => {
    const target = event.target;
    if (target && isEditableNode(target)) {
      return;
    }
    const direction = getCtrlZoomDirection(event);
    if (!direction) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (direction > 0) {
      zoom.zoomIn!();
    } else {
      zoom.zoomOut!();
    }
  };
  const onWheel = (event: WheelEvent) => {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    const target = event.target;
    if (target && isEditableNode(target)) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.deltaY < 0) {
      zoom.zoomIn!();
    } else if (event.deltaY > 0) {
      zoom.zoomOut!();
    }
  };
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('wheel', onWheel, { capture: true, passive: false });
  pdfViewerZoomInputCleanup = () => {
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('wheel', onWheel, true);
  };
}

export function setupEmbedPdfViewerUi(
  registry: PluginRegistry,
  pageNumber: number,
  features: PdfViewerFeaturePermissions = PDF_VIEWER_PERMISSIONS_CONFIG,
): void {
  forceEmbedPdfInstantNavigation(registry);
  hideEmbedPdfOrphanSeparators(registry, features);
  openEmbedPdfSidebarByDefault(registry);
  injectAosphereToolbarLogo();
  installPdfViewerZoomPersistence(registry);
  enableEmbedPdfDocumentLinkNavigation(registry);
  configureEmbedPdfTextSelection(registry, features.showCopy);
  scrollEmbedPdfToPage(registry, pageNumber);
}

/** After an invalid PDF error, EmbedPDF's "Close Document" closes the doc — redirect home. */
export function installPdfViewerInvalidDocumentRedirect(
  registry: PluginRegistry,
  onCloseAfterError: () => void,
): () => void {
  const documentManager = getPluginProvides<DocumentManagerCapability>(registry, 'document-manager');
  if (!documentManager) {
    return () => {};
  }

  let hadDocumentError = false;

  const unsubscribeError = documentManager.onDocumentError(() => {
    hadDocumentError = true;
  });

  const unsubscribeClosed = documentManager.onDocumentClosed(() => {
    if (!hadDocumentError) {
      return;
    }

    hadDocumentError = false;
    onCloseAfterError();
  });

  return () => {
    unsubscribeError();
    unsubscribeClosed();
  };
}
